import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Badge,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  StorefrontOutlined as StoreIcon,
  Circle as CircleIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';
import axios from 'axios';

const NotificationsManager = () => {
  const { state, actions } = useApp();
  const [activeUsers, setActiveUsers] = useState([]);
  const [roleCount, setRoleCount] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [notification, setNotification] = useState({
    title: '',
    message: '',
    type: 'info',
    actionUrl: '',
    dismissible: true
  });

  const [sendMode, setSendMode] = useState('role'); // 'role', 'all'
  const [selectedRole, setSelectedRole] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch active users
  const fetchActiveUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${appConfig.api.baseURL}/notifications/active-users`
      );

      if (response.data.success) {
        setActiveUsers(response.data.data.activeUsers);
        setRoleCount(response.data.data.roleCount);
      }
    } catch (error) {
      console.error('Error fetching active users:', error);
      actions.showSnackbar('Failed to fetch active users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch notification history
  const fetchNotificationHistory = async () => {
    try {
      const token = localStorage.getItem(appConfig.storage.token);
      const response = await axios.get(
        `${appConfig.api.baseURL}/notifications/history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setNotificationHistory(response.data.data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notification history:', error);
      actions.showSnackbar('Failed to fetch notification history', 'error');
    }
  };

  // Send notification
  const sendNotification = async () => {
    if (!notification.title || !notification.message) {
      actions.showSnackbar('Title and message are required', 'warning');
      return;
    }

    try {
      let endpoint = '';
      let payload = { ...notification };

      switch (sendMode) {
        case 'role':
          if (!selectedRole) {
            actions.showSnackbar('Please select a role', 'warning');
            return;
          }
          endpoint = '/notifications/send-to-role';
          payload.role = selectedRole;
          break;
        case 'all':
          endpoint = '/notifications/send-to-all';
          break;
        default:
          actions.showSnackbar('Invalid send mode', 'error');
          return;
      }

      const response = await axios.post(
        `${appConfig.api.baseURL}${endpoint}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        actions.showSnackbar(response.data.message, 'success');
        setSendDialogOpen(false);
        resetNotificationForm();
        fetchActiveUsers(); // Refresh active users
      } else {
        actions.showSnackbar(response.data.message || 'Failed to send notification', 'error');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      actions.showSnackbar('Failed to send notification', 'error');
    }
  };

  // Reset notification form
  const resetNotificationForm = () => {
    setNotification({
      title: '',
      message: '',
      type: 'info',
      actionUrl: '',
      dismissible: true
    });
    setSelectedRole('');
    setSendMode('role');
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
      case 'super-admin':
        return <AdminIcon color="error" />;
      case 'admin':
        return <AdminIcon color="warning" />;
      case 'store_admin':
        return <StoreIcon color="info" />;
      case 'user':
      default:
        return <PersonIcon color="action" />;
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
      case 'super-admin':
        return 'error';
      case 'admin':
        return 'warning';
      case 'store_admin':
        return 'info';
      case 'user':
      default:
        return 'default';
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    fetchActiveUsers();
    
    if (autoRefresh) {
      const interval = setInterval(fetchActiveUsers, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Notifications Manager
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="primary"
              />
            }
            label="Auto Refresh"
          />
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchActiveUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => {
              fetchNotificationHistory();
              setHistoryDialogOpen(true);
            }}
          >
            History
          </Button>
          
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setSendDialogOpen(true)}
          >
            Send Notification
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Total Active Users
              </Typography>
              <Typography variant="h3" color="primary">
                {activeUsers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {Object.entries(roleCount).map(([role, count]) => (
          <Grid item xs={12} md={3} key={role}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getRoleIcon(role)}
                  <Typography variant="h6" color="text.secondary">
                    {role.replace('_', ' ').toUpperCase()}
                  </Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Active Users Table */}
      <Card>
        <CardHeader
          title="Active Users"
          subheader={`${activeUsers.length} users currently connected`}
        />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Connected At</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeUsers.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRoleIcon(user.role)}
                        <Box>
                          <Typography variant="body1">{user.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.userId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.replace('_', ' ')}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.connectedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {Math.floor((Date.now() - new Date(user.connectedAt)) / 60000)}m
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircleIcon sx={{ color: 'success.main', fontSize: 12 }} />
                        <Typography variant="body2" color="success.main">
                          Online
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Send Notification Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Send Mode Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Send To</InputLabel>
              <Select
                value={sendMode}
                onChange={(e) => setSendMode(e.target.value)}
                label="Send To"
              >
                <MenuItem value="role">All Users with Role</MenuItem>
                <MenuItem value="all">All Active Users</MenuItem>
              </Select>
            </FormControl>

            {/* Role Selection */}
            {sendMode === 'role' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="store_admin">Store Admin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Notification Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Title"
                value={notification.title}
                onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
                required
                variant="outlined"
                helperText="Enter a clear and concise title for the notification"
              />
              
              <TextField
                fullWidth
                label="Message"
                value={notification.message}
                onChange={(e) => setNotification(prev => ({ ...prev, message: e.target.value }))}
                multiline
                rows={4}
                required
                variant="outlined"
                helperText="Enter the notification message content"
              />
              
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={notification.type}
                  onChange={(e) => setNotification(prev => ({ ...prev, type: e.target.value }))}
                  label="Type"
                >
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Action URL (optional)"
                value={notification.actionUrl}
                onChange={(e) => setNotification(prev => ({ ...prev, actionUrl: e.target.value }))}
                placeholder="/dashboard"
                variant="outlined"
                helperText="Optional URL to redirect when 'View' is clicked"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={notification.dismissible}
                    onChange={(e) => setNotification(prev => ({ ...prev, dismissible: e.target.checked }))}
                  />
                }
                label="Allow users to dismiss this notification"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button onClick={sendNotification} variant="contained" startIcon={<SendIcon />}>
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Notification History</DialogTitle>
        <DialogContent>
          <List>
            {notificationHistory.map((notif) => (
              <ListItem key={notif.id} divider>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={notif.title}
                  secondary={
                    <Box>
                      <Typography variant="body2">{notif.message}</Typography>
                      <Typography variant="caption">
                        Sent by: {notif.sender} | {new Date(notif.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsManager; 